import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpEventType,
  HttpUploadProgressEvent,
} from '@angular/common/http';
import { Observable, filter, map, tap } from 'rxjs';
import { ApiRequest, ApiResponse } from '../models';
import { APP_CONFIG } from '@core/config';

export type HttpValue = string | number | boolean | Blob | File | Date;
export type KeyValue = Record<string, HttpValue | HttpValue[]>;
export type Lazy<T> = T | (() => T);

export interface RequestOptions {
  params?: Lazy<Record<string, HttpValue> | HttpParams>;
  headers?: Lazy<Record<string, string> | HttpHeaders>;
  context?: HttpContext;
  signal?: AbortSignal;
}

@Injectable({
  providedIn: 'root',
})
export class HttpRestService {
  private _http = inject(HttpClient);
  private readonly _appConfig = inject(APP_CONFIG);
  private readonly _baseUrl = `${this._appConfig.apiUrl}`;

  get<T>(url: string, opts?: RequestOptions): Observable<T> {
    return this._http
      .get<ApiResponse<T>>(`${this._baseUrl}/${url}`, this.buildOptions(opts))
      .pipe(map((response) => response.data));
  }

  delete<T>(url: string, body?: unknown, opts?: RequestOptions): Observable<T> {
    return this._http
      .delete<ApiResponse<T>>(`${this._baseUrl}/${url}`, {
        ...this.buildOptions(opts),
        body: body !== undefined ? this.createApiRequest(body) : undefined,
      })
      .pipe(map((response) => response.data));
  }

  post<T, B = unknown>(
    url: string,
    body: B,
    opts?: RequestOptions,
  ): Observable<T> {
    return this._http
      .post<
        ApiResponse<T>
      >(`${this._baseUrl}/${url}`, this.createApiRequest(body), this.buildOptions(opts))
      .pipe(map((response) => response.data));
  }

  postWithoutBody<T>(url: string, opts?: RequestOptions): Observable<T> {
    return this._http
      .post<
        ApiResponse<T>
      >(`${this._baseUrl}/${url}`, null, this.buildOptions(opts))
      .pipe(map((response) => response.data));
  }

  put<T, B = unknown>(
    url: string,
    body: B,
    opts?: RequestOptions,
  ): Observable<T> {
    return this._http
      .put<
        ApiResponse<T>
      >(`${this._baseUrl}/${url}`, this.createApiRequest(body), this.buildOptions(opts))
      .pipe(map((response) => response.data));
  }

  patch<T, B = unknown>(
    url: string,
    body: B,
    opts?: RequestOptions,
  ): Observable<T> {
    return this._http
      .patch<
        ApiResponse<T>
      >(`${this._baseUrl}/${url}`, this.createApiRequest(body), this.buildOptions(opts))
      .pipe(map((response) => response.data));
  }

  postForm<T>(
    url: string,
    data: KeyValue,
    opts?: RequestOptions & {
      onUploadProgress?: (event: HttpUploadProgressEvent) => void;
    },
  ): Observable<T> {
    const fd = this.toFormData(data);
    if (opts?.onUploadProgress) {
      return this._http
        .post<T>(`${this._baseUrl}/${url}`, fd, {
          ...this.buildOptionsForFormData(opts),
          observe: 'events',
          reportProgress: true,
        })
        .pipe(
          tap((event) => {
            if (event.type === HttpEventType.UploadProgress) {
              opts.onUploadProgress?.(event);
            }
          }),
          filter((event) => event.type === HttpEventType.Response),
          map((event) => event.body as T),
        );
    }
    return this._http.post<T>(
      `${this._baseUrl}/${url}`,
      fd,
      this.buildOptionsForFormData(opts),
    );
  }

  putForm<T>(
    url: string,
    data: KeyValue,
    opts?: RequestOptions,
  ): Observable<T> {
    const fd = this.toFormData(data);
    return this._http.put<T>(
      `${this._baseUrl}/${url}`,
      fd,
      this.buildOptionsForFormData(opts),
    );
  }

  patchForm<T>(
    url: string,
    data: KeyValue,
    opts?: RequestOptions,
  ): Observable<T> {
    const fd = this.toFormData(data);
    return this._http.patch<T>(
      `${this._baseUrl}/${url}`,
      fd,
      this.buildOptionsForFormData(opts),
    );
  }

  private evaluateLazy<T>(value?: Lazy<T>, ctx = 'value'): T | undefined {
    if (value === undefined) return undefined;
    if (typeof value === 'function') {
      try {
        return (value as () => T)();
      } catch (err) {
        throw new Error(
          `Error evaluating lazy ${ctx}: ${(err as Error).message}`,
        );
      }
    }
    return value;
  }

  private ensurePlainObject<T extends Record<string, unknown>>(
    v: unknown,
    ctx = 'object',
  ): T {
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      throw new Error(`Invalid ${ctx}: expected a plain object.`);
    }
    return v as T;
  }
  private toParams(
    src?: Lazy<Record<string, HttpValue> | HttpParams>,
  ): HttpParams | undefined {
    const value = this.evaluateLazy(src, 'params');
    if (value === undefined || value === null) return undefined;
    if (value instanceof HttpParams) return value;

    const obj = this.ensurePlainObject(value, 'params') as Record<
      string,
      HttpValue | HttpValue[]
    >;
    let params = new HttpParams();

    for (const key of Object.keys(obj)) {
      const raw = obj[key];

      if (raw === null || raw === undefined) continue;

      if (Array.isArray(raw)) {
        for (const item of raw) {
          if (item === null || item === undefined) continue;
          if (item instanceof Blob || item instanceof File) {
            throw new Error(
              `Invalid params value for '${key}': File/Blob cannot be used as query param.`,
            );
          }
          params = params.append(key, this.serializeParamValue(item));
        }
      } else {
        if (raw instanceof Blob || raw instanceof File) {
          throw new Error(
            `Invalid params value for '${key}': File/Blob cannot be used as query param.`,
          );
        }
        params = params.append(key, this.serializeParamValue(raw));
      }
    }

    return params;
  }

  private serializeParamValue(v: HttpValue): string {
    if (v instanceof Date) return v.toISOString();
    return String(v);
  }

  private toHeaders(
    src?: Lazy<Record<string, string> | HttpHeaders>,
  ): HttpHeaders | undefined {
    const value = this.evaluateLazy(src, 'headers');
    if (value === undefined || value === null) return undefined;
    if (value instanceof HttpHeaders) return value;

    const obj = this.ensurePlainObject(value, 'headers') as Record<
      string,
      string
    >;
    let headers = new HttpHeaders();

    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (val === null || val === undefined) continue;
      headers = headers.append(key, String(val));
    }
    return headers;
  }

  private buildOptions(opts?: RequestOptions) {
    return {
      params: this.toParams(opts?.params),
      headers: this.toHeaders(opts?.headers),
      context: opts?.context,
      signal: opts?.signal,
    };
  }

  private buildOptionsForFormData(opts?: RequestOptions) {
    const base = this.buildOptions(opts);
    if (base.headers) {
      if (base.headers.has && base.headers.has('Content-Type')) {
        base.headers = base.headers.delete('Content-Type');
      }
    }
    return base;
  }

  private appendFormValue(fd: FormData, key: string, value: HttpValue) {
    if (value === null || value === undefined) return;

    if (value instanceof Blob || value instanceof File) {
      fd.append(key, value);
      return;
    }

    if (value instanceof Date) {
      fd.append(key, value.toISOString());
      return;
    }

    fd.append(key, String(value));
  }

  private toFormData(data: KeyValue): FormData {
    const obj = this.ensurePlainObject(data, 'formData') as KeyValue;
    const fd = new FormData();

    for (const key of Object.keys(obj)) {
      const raw = obj[key];
      if (raw === null || raw === undefined) continue;

      if (Array.isArray(raw)) {
        for (const item of raw) {
          if (item === null || item === undefined) continue;
          this.appendFormValue(fd, key, item);
        }
      } else {
        this.appendFormValue(fd, key, raw);
      }
    }

    return fd;
  }

  private createApiRequest<T>(data: T, context = ''): ApiRequest<T> {
    return { context, data };
  }
}
